// @ts-nocheck
import { graphql, navigate, Slice } from "gatsby"
import { useEffect, useState, useCallback } from "react"
import { Typography, Box } from "@mui/material"
import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"
import { gsap } from "@ggs/ui/local/gsap"
import { get, isEmpty } from "lodash"
import dayjs from "dayjs"

import {
  ContactCTA,
  ContentHighlight,
  DynamicSubNav,
  GlobalAdBanners,
  Hero2,
  SectionSwitcher,
  StickyTripBanner,
  SuggestedTrips,
  TripItinerary,
  TripMapOverview,
  VisualMediaContent,
  SpecialTag,
  TripModal,
} from "../components"
import RawSectionSwitcher from "../components/SectionSwitcher/RawSectionSwitcher"
import SEOHead from "../components/head"
import Layout from "../components/layout"
import {
  mapArticleTeaser,
  mapSectionSwitcher,
  mapTripItinerary,
  mapTripItineraryMapItems,
  mapWishlistData,
} from "../helpers/data-mappers"
import { useGlobalAdsSolrData, useStaffData, useTripsData, useUserLocation } from "../local/hooks"
import { getPricePrefix } from "@ggs/utils"



const TripDetailView = (props) => {
  const trip = get(props, "data.nodeTrip", {})
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const userCountry = useSelector((state) => state.settings.country)
  const { mapCountryToCurrency } = useUserLocation()
  const currency = mapCountryToCurrency(userCountry?.code)
  const [suggestedTrips, setSuggestedTrips] = useState(null)
  const [isPageVisible, setIsPageVisible] = useState(null)
  const [isSpecialCategory, setIsSpecialCategory] = useState(false)
  const [tripSavings, setTripSavings] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip) => trip.nid)
  const currentTripId = trip?.external_id || ""
  const refCountryId = trip?.primary_country.external_id || ""
  const refDestinationId = get(trip?.primary_country, "destinations.[0].external_id", "")
  const tripDuration = trip?.duration_override || get(trip?.package_code, "package_duration", "")
  const { getCountryGlobalAds, getDestinationGlobalAds } = useGlobalAdsSolrData()
  const { getRandomStaff } = useStaffData()
  const { getTripsByNid, getTripsByCountryAndDestination } = useTripsData()
  const [globalAds, setGlobalAds] = useState(null)
  const [contactCtaStaff, setContactCtaStaff] = useState(null)
  const [isArticleLiked, setIsArticleLiked] = useState(
    (likedTrips && likedTrips?.includes(trip.external_id)) || false
  )
  const [isTripLiked, setIsTripLiked] = useState(
    (likedTrips && likedTrips?.includes(trip.external_id)) || false
  )
  const [liveTrip, setLiveTrip] = useState(null)
  const mappedItinerary = mapTripItinerary(trip?.package_code)
  const mappedItineraryMapItems = mapTripItineraryMapItems(
    trip?.package_code,
    trip?.geolocation_override
  )
  const getBookByDate = () => {
    if (trip?.special_offer && trip?.special_offer_scheduler) {
      const isDateExpired = dayjs().isAfter(dayjs(trip?.special_offer_scheduler))
      return isDateExpired ? null : dayjs(trip?.special_offer_scheduler).format("MM/DD/YY")
    }
    return null
  }


  useEffect(() => {
    if (likedTrips && likedTrips?.includes(currentTripId)) {
      setIsTripLiked(true)
    } else {
      setIsTripLiked(false)
    }
  }, [likedTrips, currentTripId])

  useEffect(() => {
    getTripsByCountryAndDestination(
      refDestinationId,
      refCountryId,
      trip?.suggested_trips,
      currentTripId
    ).then((data) => {
      const filteredData = mapWishlistData(likedTrips, data)
      setSuggestedTrips({
        title: trip?.suggested_trips?.title || "",
        subtitle: trip?.suggested_trips?.subtitle || "",
        link: { title: "All Trips", uri: trip.slug.split('/trips')[0]+ "/vacation-packages" },
        items: filteredData,
      })
    })

    if (currentTripId) {
      getTripsByNid([currentTripId], 1).then((liveTrips) => {
        if (liveTrips && liveTrips.length > 0) {
          setLiveTrip(liveTrips[0])
        }
      })
    }
  }, [userCountry])

  // Resolve Solr data.
  useEffect(() => {
    resolveGlobalAds()
    getRandomStaff().then((data) => {
      setContactCtaStaff(data)
    })
  }, [])

  const addToWishList = (selectedEntity) => {
    if (selectedEntity) {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, true)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    } else {
      setIsArticleLiked(true)
    }
    dispatch(
      addNode({
        type: selectedEntity ? selectedEntity.contentType : "trip",
        entity: {
          contentType: selectedEntity ? selectedEntity.contentType : "trip",
          nid: selectedEntity ? selectedEntity.nid : trip.external_id,
        },
        idPropertyKey: "nid",
      })
    )
  }

  const removeFromWishList = (selectedEntity) => {
    if (selectedEntity) {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, false)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    } else {
      setIsArticleLiked(false)
    }
    dispatch(
      removeNode({
        type: selectedEntity ? selectedEntity.contentType : "trip",
        findPredicate: {
          contentType: selectedEntity ? selectedEntity.contentType : "trip",
          nid: selectedEntity ? selectedEntity.nid : trip.external_id,
        },
      })
    )
  }

  const subNav = [
    {
      title: "Trip Overview",
      uri: "#trip-section-1",
    },
    {
      title: "Itinerary",
      uri: "#trip-section-2",
    },
    {
      title: "Prices & Inclusions",
      uri: "#trip-section-3",
    },
    {
      title: `${trip?.primary_country.title} Trips`,
      uri: `${trip?.primary_country.slug}/trip-itineraries`,
    },
  ]

  const resolveGlobalAds = async () => {
    const countryAds = await getCountryGlobalAds(refCountryId)
    setGlobalAds(countryAds)
    // When no ads available for country, get ads for destination.
    if (isEmpty(countryAds)) {
      const destinationAds = await getDestinationGlobalAds(refDestinationId)
      setGlobalAds(destinationAds)
    }
  }

  const stickyTripBannerProps = {
    id: trip?.package_code?.title,
    title: trip.title,
    image: trip.hero_image,
    slug: trip.slug,
    duration: tripDuration,
    price: liveTrip?.rawPrice,
    currency: currency,
    primaryCountry: get(trip?.primary_country, "title", null),
    destinationSlug: get(trip?.primary_country?.destinations?.[0], "slug", null),
    savings: isSpecialCategory ? tripSavings : null,
    deadline: getBookByDate(),
  }

  const headerTripData = {
    image: trip.hero_image,
    title: trip.title,
    slug: trip.slug,
    primaryCountry: get(trip?.primary_country, "title", null),
    destinationSlug: get(trip?.primary_country?.destinations?.[0], "slug", null),
    id: trip?.package_code?.title,
  }

  const breadcrumbs = [
    {
      title: "Home",
      uri: "/",
    },
    {
      title: "Destinations",
      uri: "/destinations",
    },
    {
      title: trip?.primary_country?.destinations?.[0].title,
      uri: trip?.primary_country?.destinations?.[0].slug,
    },
    {
      title: trip?.primary_country?.title,
      uri: trip?.primary_country?.slug,
    },
    {
      title: trip?.package_code?.package_title,
    },
  ]

  const tags = [
    ...(trip?.special_offer ? [{ label: "Specials", color: "orange" }] : []),
    {
      label: trip?.primary_country.title,
      color: "blue",
      uri: trip?.primary_country.self_link?.uri,
    },
  ]

  // Scroll to section on anchor click
  useEffect(() => {
    const anchors = document.querySelectorAll('a[href^="#"]')

    const handleClick = (e) => {
      e.preventDefault()
      const targetId = e.currentTarget.getAttribute("href")
      
      // Remove the # from the beginning to get just the ID
      const elementId = targetId.substring(1)
      
      // Use CSS.escape if available, otherwise fallback to manual escaping
      let escapedId
      if (typeof CSS !== 'undefined' && CSS.escape) {
        escapedId = CSS.escape(elementId)
      } else {
        // Manual escaping for common special characters
        escapedId = elementId.replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, "\\$&")
      }
      
      const targetElement = document.querySelector(`#${escapedId}`)

      if (targetElement) {
        gsap.to(window, {
          duration: 1,
          scrollTo: {
            y: targetElement,
            offsetY: 70,
          },
        })
      }
    }

    setTimeout(() => {
      anchors.forEach((anchor) => {
        anchor.addEventListener("click", handleClick)
      })
    }, 100) // Delay to ensure elements are available

    // Cleanup function to remove event listeners
    return () => {
      anchors.forEach((anchor) => {
        anchor.removeEventListener("click", handleClick)
      })
    }
  }, [subNav])

  // Check if page is visible for the user country
  useEffect(() => {
    if (trip?.page_visibility_segmentation && userCountry) {
      switch (trip?.page_visibility_segmentation) {
        case "CA":
          setIsPageVisible(userCountry?.code === "CA")
          break
        case "AU":
          setIsPageVisible(userCountry?.code === "AU")
          break
        case "NZ":
          setIsPageVisible(userCountry?.code === "NZ")
          break
        case "GB":
          setIsPageVisible(userCountry?.code === "UK")
          break
        case "EU":
          setIsPageVisible(userCountry?.isEurope && userCountry?.code !== "UK")
          break
        default:
          setIsPageVisible(true)
      }
    }
  }, [trip?.page_visibility_segmentation, userCountry])

  // Redirect to 404 if page is not available for users country
  useEffect(() => {
    if (isPageVisible === false) {
      navigate("/404")
    }
  }, [isPageVisible])

  // Check if user is in special category
  useEffect(() => {
    if (trip?.special_category_geolocation && userCountry) {
      switch (trip?.special_category_geolocation) {
        case "CA":
          setIsSpecialCategory(userCountry?.code === "CA")
          break
        case "AU":
          setIsSpecialCategory(userCountry?.code === "AU")
          break
        case "NZ":
          setIsSpecialCategory(userCountry?.code === "NZ")
          break
        case "GB":
          setIsSpecialCategory(userCountry?.code === "UK")
          break
        case "EU":
          setIsSpecialCategory(userCountry?.isEurope && userCountry?.code !== "UK")
          break
        default:
          setIsSpecialCategory(true)
      }
    }
  }, [trip?.special_category_geolocation, userCountry])

  useEffect(() => {
    const savings = liveTrip?.savings || ""
    if (savings.includes("$") || savings.includes("£") || savings.includes("€")) {
      setTripSavings(`savings ${savings}`)
    } else if (savings.includes("%")) {
      setTripSavings(`savings ${savings}`)
    } else if (trip?.added_value) {
      setTripSavings("Added Value")
    }
    // TODO: Check with Pierre if added value flag should be loaded through Solr live data.
  }, [liveTrip?.savings, trip?.added_value])

  return (
    <>
      {ComposeHead(props)}
      {!isPageVisible ? null : (
        <Layout
          showChatButton={false}
          hideOnScroll={true}
          trip={headerTripData}
          hero={
            <Hero2
              liked={isTripLiked}
              onLike={addToWishList}
              onUnlike={removeFromWishList}
              title={trip.title}
              image={trip.hero_image}
              duration={tripDuration ? `${tripDuration} days` : null}
              price={liveTrip?.rawPrice}
              maxPrice={liveTrip?.rawMaxPrice}
              currency={currency}
              specials={{
                savings: liveTrip?.savings,
                deadline: liveTrip?.bookDate,
                addedValue: trip?.added_value,
                specialOfferShortDescription: liveTrip?.specialOfferShortDescription,
              }}
              tags={tags}
              path={breadcrumbs}
              onClickTripCta={() => setIsModalOpen(true)}
            />
          }
        >
          <TripMapOverview
            title={trip.trip_overview_title}
            description={trip.trip_overview_description}
            special={trip.special_offer_description}
            deadline={getBookByDate()}
            items={mappedItineraryMapItems}
            tripCategories={liveTrip?.tripCategories}
          />
          <VisualMediaContent images={trip.vmedia_content?.media} />
          {trip?.package_code && <TripItinerary {...mappedItinerary} />}
          <ContentHighlight
            color={trip.content_highlight?.background_color}
            title={trip.content_highlight?.title}
            subtitle={trip.content_highlight?.subtitle}
            description={trip.content_highlight?.description}
            image={trip.content_highlight?.image ?? trip.hero_image}
          />
          {(() => {
            // PRIORITY: Use raw data if available, fallback to GraphQL data
            if (trip.raw_drupal_relationships?.section_switcher?.data?.relationships?.items?.data?.length > 0) {
              return <RawSectionSwitcher rawData={trip.raw_drupal_relationships.section_switcher} />
            } else if (trip.section_switcher?.items) {
              const mappedItems = mapSectionSwitcher(trip.section_switcher.items)
              return <SectionSwitcher items={mappedItems} />
            } else {
              return null
            }
          })()}
          {globalAds && <GlobalAdBanners items={globalAds} />}
          {suggestedTrips && (
            <SuggestedTrips
              {...suggestedTrips}
              onLike={addToWishList}
              onUnlike={removeFromWishList}
            />
          )}
          <ContactCTA
            title={trip.contact_cta?.title}
            subtitle={trip.contact_cta?.subtitle}
            description={trip.contact_cta?.description}
            link={trip.contact_cta?.link}
            image={trip.contact_cta?.image}
            staff={contactCtaStaff}
          />
          <Slice alias="preFooterStyle2" />
          <StickyTripBanner
            tripTitle={trip.title}
            bookDate={getBookByDate()}
            savings={liveTrip?.savings}
            addedValue={trip?.added_value}
            specialOfferShortDescription={liveTrip?.specialOfferShortDescription}
            duration={tripDuration ? `${tripDuration} days` : null}
            price={liveTrip?.rawPrice}
            maxPrice={liveTrip?.rawMaxPrice}
            prefix={getPricePrefix(currency as string)}
            onClick={() => setIsModalOpen(true)}
          />
          <TripModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            trip={{
              id: trip.external_id,
              title: trip.title,
              image: trip.hero_image,
              slug: trip.slug,
              primaryCountry: trip.primary_country.title,
              destinationSlug: trip.primary_country.destinations[0].slug,
            }}
          />
        </Layout>
      )}
    </>
  )
}

export const ComposeHead = (props) => {
  const { nodeTrip } = props.data
  const metatag = get(nodeTrip, "metatag.items")
  const ImageUrl = get(nodeTrip, "hero_image.variants[0].url", "")
  const url = get(nodeTrip, "slug")
  return <SEOHead metas={metatag} url={url} image={ImageUrl} />
}

export const query = graphql`
  query ($id: String!) {
    nodeTrip(id: { eq: $id }) {
      id
      slug
      title
      trip_overview_description
      added_value
      special_offer
      special_offer_scheduler
      special_offer_description
      duration_override
      page_visibility_segmentation
      special_category_geolocation
      save_percent
      save_amount
      save_amount_aud
      save_amount_eud
      save_amount_gbd
      save_amount_nzd
      save_amount_usd
      trip_overview_title
      external_id
      metatag {
        items {
          content
          name
        }
      }
      hero_image {
        variants {
          style
          title
          url
          width
          alt
        }
      }
      contact_cta {
        ...ContactCtaFragment
      }
      suggested_trips {
        title
        subtitle
      }
      vmedia_content {
        ...VisualMediaContentFragment
      }
      country {
        title
        self_link {
          uri
        }
        external_id
        destinations {
          external_id
        }
      }
      primary_country {
        id
        title
        external_id
        slug
        destinations {
          title
          external_id
          slug
        }
      }
      content_highlight {
        ...ContentHighlightFragment
      }
      section_switcher {
        ...SectionSwitcherFragment
      }
      raw_drupal_relationships
      suggested_trips {
        ...SuggestedTripsFragment
      }
      price_band
      duration_override
      geolocation_override {
        location_title
        paragraph_geolocation {
          lat
          lng
        }
      }
      package_code {
        title
        package_price
        package_price_aus
        package_price_cad
        package_price_eur
        package_price_gbp
        package_price_nzd
        package_price_usd
        package_title
        package_duration
        p_code
        p_package_id
        p_days {
          day_number
          day_package_id
          day_title
          day_component {
            c_description
            c_type
            c_all_departure_dates
            c_arrival_parent_id
            c_arrival_parent_type
            c_car_type
            c_component_id
            c_default_meal_basis
            c_departure_parent_id
            c_departure_parent_type
            c_description_multiday
            c_customer_description
            c_dropofftime
            c_duration
            c_end_day
            c_extra_category
            c_extra_id
            c_extra_type
            c_extra_type_id
            c_location
            c_meal
            c_name
            c_origin_depot_id
            c_pickuptime
            c_reference
            c_sequence
            c_start_day
            c_summary
            c_title
            c_arrival_location_name {
              title
            }
            c_departure_location_name {
              title
            }
            c_property_reference_id {
              id
              title
              summary
              description
              rating
              accomodation_image
              geolocation {
                lat
                lng
              }
            }
          }
        }
      }
    }
  }
`

export default TripDetailView
